/**
 * The scorer app's entry point, and the login gate (ADR-0008).
 *
 * Two things meet here.
 *
 * **Local-first (#13).** Every ball is on disk before the UI confirms it, the outbox drains
 * to the server when there is a network, and neither the scorer nor the scorecard waits on
 * the other (ADR-0010). Reloading mid-match loses nothing. The store and the transport are
 * constructed here and injected downwards, because they are the two platform-specific
 * pieces — `@sportsos/sync` knows about neither, which is what lets React Native reuse the
 * sync logic wholesale in P1.
 *
 * **An account in front of it (#14).** A scorer must be able to come back tomorrow and find
 * his matches, and a browser-only session survives neither a cleared cookie nor a new phone.
 * The session is *not* state this app owns: it is an httpOnly cookie the browser will not
 * let us read, so the only way to know who is signed in is to ask — `GET /api/me` on boot,
 * 200 or 401. That single call is also why sessions survive a reload for free, because
 * nothing was ever held here to rehydrate.
 *
 * The two are deliberately independent: a match on this device stays readable and scoreable
 * whatever the session says, and signing out ends a session without touching a ledger. What
 * the gate withholds is the *screens*, not the data.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type { Event, PlayerId, RuleSet } from "@sportsos/engine";
import { api, installReadCache, publicMatchPath } from "./api.ts";
import { startCacheRefresh } from "./cache/backgroundRefresh.ts";
import { readCache, refreshReadCache } from "./cache/install.ts";
import { bootSession, hintAction } from "./auth/bootSession.ts";
import type { BootAnswer } from "./auth/bootSession.ts";
import { beginGuest, endGuest, isGuest } from "./auth/guest.ts";
import { friction, reportFriction } from "./auth/observer.ts";
import { inviteToRemember, inviteToReturnTo, isSignedIn, rememberInvite, takeInvite } from "./pendingInvite.ts";
import { localStore } from "./localStore.ts";
import { buildFirstInnings } from "./match/draft.ts";
import type { MatchSetup } from "./match/setup.ts";
import { savedDefaults } from "./match/setup.ts";
import { useLedger } from "./match/useLedger.ts";
import { addressFor, pathFor, routeFor, tabFor, type Tab } from "./route.ts";
import { screenFor, showsTabBar, type Destination, type Session } from "./screen.ts";
import { applyTheme, applyThemeColor, otherTheme, readTheme, writeTheme, type Theme } from "./theme.ts";
import { registerServiceWorker, whenUpdateReady } from "./sw/register.ts";
import { captureInstallPrompt } from "./install.ts";
import { setCurrentUserId } from "./identity.ts";
import { canPersist } from "./storage/db.ts";
import { loadIdentityHint, saveIdentityHint } from "./storage/identityHint.ts";
import { clearCurrent, loadCurrentSetup, loadSetup, makeCurrent, saveSetup } from "./storage/matches.ts";
import { drainer, eventStore } from "./sync/drainer.ts";
import { requestBackgroundDrain } from "./sync/background.ts";
import { startForegroundDrain } from "./sync/foreground.ts";
import { AIAssistant } from "./ui/AIAssistant.tsx";
import { QueueBanner } from "./ui/QueueBanner.tsx";
import { useOnline } from "./ui/useOnline.ts";
import { BannerStack } from "./ui/BannerStack.tsx";
import { GuestBanner } from "./ui/GuestBanner.tsx";
import { InstallBanner } from "./ui/InstallBanner.tsx";
import { Home } from "./ui/Home.tsx";
import { MatchDetail } from "./ui/MatchDetail.tsx";
import { Profile } from "./ui/Profile.tsx";
import { ScoringScreen } from "./ui/ScoringScreen.tsx";
import { Settings } from "./ui/Settings.tsx";
import { Setup } from "./ui/Setup.tsx";
import { SignIn } from "./ui/SignIn.tsx";
import { TabBar } from "./ui/TabBar.tsx";
import { BottomSheet } from "./ui/BottomSheet.tsx";
import { EditProfileSheet } from "./ui/EditProfileSheet.tsx";
import { MyTeams } from "./ui/MyTeams.tsx";
import { JoinTeamPage } from "./ui/TeamInvite.tsx";
import { TournamentCreate } from "./ui/TournamentCreate.tsx";
import { TournamentDetail } from "./ui/TournamentDetail.tsx";
import { Tournaments } from "./ui/Tournaments.tsx";
import "./styles.css";

/** Which destination a tab lands on. The inverse of `tabFor`, for the three tabs. */
const DESTINATION_FOR_TAB: Readonly<Record<Tab, Destination>> = {
  home: "home",
  team: "profile",
  stats: "tournaments",
  account: "settings",
};

/**
 * The scoring screen, once a match exists.
 *
 * Split out so `useLedger` can be called with a `setup` that is guaranteed to exist — a hook
 * cannot be conditional, and threading a nullable setup through it would mean a store keyed
 * on nothing.
 */
function Scoring({
  setup,
  dark,
  onToggleTheme,
  onExit,
  onMatchCompleted,
  onSignIn,
}: {
  readonly setup: MatchSetup;
  readonly dark: boolean;
  readonly onToggleTheme: () => void;
  readonly onExit: () => void;
  readonly onMatchCompleted: () => void;
  readonly onSignIn: () => void;
}): React.ReactElement {
  const ledger = useLedger({
    setup,
    // The page's one store and drainer, shared with the app-level foreground drain.
    store: eventStore,
    drainer,
    // A brand-new match opens its first innings through the same path as every other event:
    // built, written, then rendered.
    seed: (draft): readonly Event[] => [buildFirstInnings(draft.setup)],
  });

  if (ledger.hydrating) {
    // The one legitimate wait in the app, and it is before the loop rather than inside it:
    // rendering a scoring screen against an empty ledger while disk is still being read
    // would show a scorer 0/0 for a match that is 140/3.
    return <div className="setup-page">Reading this match off the device…</div>;
  }

  return (
    <ScoringScreen
      ledger={ledger}
      dark={dark}
      onToggleTheme={onToggleTheme}
      onExit={onExit}
      onMatchCompleted={onMatchCompleted}
      onSignIn={onSignIn}
    />
  );
}

function TournamentCreateWrapper({
  onCreated,
  onBack,
}: {
  readonly onCreated: (id: string) => void;
  readonly onBack: () => void;
}): React.ReactElement {
  const [ruleSets, setRuleSets] = useState<readonly RuleSet[]>([]);
  // Asked for again when the connection comes back. Loaded once, a page opened with no signal got
  // no formats and kept none — so the Create button stayed disabled for good even after the
  // network returned, with nothing on screen to say why.
  const online = useOnline();
  useEffect(() => {
    let live = true;
    void api
      .listRuleSets()
      .then(({ ruleSets: rs }) => {
        if (live) setRuleSets(rs);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [online]);
  return <TournamentCreate ruleSets={ruleSets} onCreated={onCreated} onBack={onBack} />;
}

// Reads show the last copy the server gave when it cannot be reached — per account, bounded, and
// told to the scorer. See `cache/readCache.ts` for the rules and why this is not the worker's job.
installReadCache(readCache);

function App(): React.ReactElement {
  const [session, setSessionState] = useState<Session>("loading");
  /**
   * Set the session **and who it is**, in the same breath.
   *
   * The identity used to be set in an effect after the session changed. That is one render too
   * late: React runs a child's effects before its parent's, so a screen that starts a read the
   * moment it mounts — the scorecard, after an offline reload — asked the read cache who was
   * signed in and was told "nobody", and bypassed it. Found by the browser test, not by
   * thinking about it.
   */
  const setSession = useCallback((next: Session): void => {
    if (typeof next === "object" && next !== null) setCurrentUserId(next.id);
    else if (next === null || next === "guest") setCurrentUserId(null);
    setSessionState(next);
  }, []);
  /**
   * The signed-in account's playing identity, or null when it has none.
   *
   * Kept beside the session rather than folded into it: `Session` is also "guest",
   * "loading" and "unreachable", and none of those can carry a player id.
   */
  const [playerId, setPlayerId] = useState<PlayerId | null>(null);
  const [setup, setSetup] = useState<MatchSetup | null>(null);
  const [pendingSetup, setPendingSetup] = useState<MatchSetup | null>(null);
  const [editField, setEditField] = useState<"phone" | "email" | null>(null);
  const [needsName, setNeedsName] = useState(false);
  // Where the scorer has navigated. Held explicitly rather than derived from whether a match
  // exists: derived, "there is a match on this device" would mean "show the pad", and the
  // matches list would be unreachable for as long as one was in progress.
  //
  // Seeded from the address bar (#34) so a reload, a bookmark and a shared `/match/:id` all
  // land where they say they do. The URL is read once here and written by `go()` below; it
  // is not a second source of truth that React re-reads on every render.
  const [route, setRoute] = useState(() => routeFor(window.location.pathname));
  const { destination, matchId, tournamentId, inviteCode } = route;
  const store = useMemo(() => localStore(), []);
  /**
   * Light or dark (#41). Seeded from the device rather than defaulted, because a preference
   * the app forgets on every reload is one the scorer re-sets forty times.
   */
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  /** True while we are still asking the device whether a match is in progress. */
  const [resuming, setResuming] = useState(true);
  /**
   * How to apply a waiting service-worker update, or null when there is nothing waiting.
   *
   * The worker never calls `skipWaiting` on its own — swapping the bundle under a scorer
   * mid-over is the debugging cost ADR-0010 warned about. So a new build waits, and this is
   * the affordance that lets someone take it between matches. It is not a nicety: today the
   * shell is served `no-cache` and a deploy lands on the next load, so removing that without
   * replacing it would pin scorers to an old bundle with no way to tell.
   */
  const [applyUpdate, setApplyUpdate] = useState<(() => void) | null>(null);
  /** The current session, readable from event handlers without re-subscribing them. */
  const sessionRef = useRef<Session>("loading");
  sessionRef.current = session;
  /** True while the session is the cached hint rather than something the server confirmed. */
  const provisionalRef = useRef(false);

  /**
   * Which screen the app shows. Computed here rather than at the point of render because the
   * address-bar effect below needs the gate's answer too — and computing it twice is how the
   * bar and the screen end up disagreeing about where the scorer is.
   */
  const screen = screenFor(session, destination, setup !== null);

  const dark = theme === "dark";
  // During render rather than in an effect, so the attribute is on `<html>` before the
  // first paint of a re-render — an effect would flash the previous theme for a frame.
  applyTheme(theme);
  // The status-bar colour of an installed app, which `index.html` can only guess at before
  // this runs: its two metas key off the OS colour scheme, and the theme is a stored
  // preference that need not agree with it. Here rather than inside `applyTheme` so that
  // function keeps taking an element and keeps being testable without a document.
  applyThemeColor(theme);
  const toggleTheme = (): void => {
    setTheme((current) => {
      const next = otherTheme(current);
      writeTheme(next);
      return next;
    });
  };

  /**
   * Navigate, and say so in the address bar.
   *
   * `pushState` rather than assigning `location.href`: a real navigation would tear down the
   * page, which on this app means dropping an outbox mid-drain and re-reading the whole
   * ledger off disk to show the same screen.
   */
  const go = useCallback((next: Destination, id: string | null = null): void => {
    // Which of the two id fields the destination fills is `routeFor`'s business, so the
    // route is rebuilt from the path rather than assembled here — one place decides what
    // `/tournament/x` means, and it is the same place on a fresh boot and on a tap.
    setRoute(routeFor(pathFor(next, id)));
    const path = pathFor(next, id);
    // Guarded, so re-selecting the tab you are already on does not stack up identical
    // history entries that the back button then has to be pressed through one at a time.
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
  }, []);

  /**
   * The back button, which now has an answer.
   *
   * Before #34 there were no history entries, so back closed the tab — on a phone carrying an
   * innings that had not finished syncing. On Android it is a system gesture, not something a
   * scorer can decide not to use.
   */
  useEffect(() => {
    const onPop = (): void => setRoute(routeFor(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /**
   * Keep the address bar honest about where the scorer actually is.
   *
   * What counts as honest is `addressFor`'s decision, not this effect's — see the note there,
   * including the bug that came of deciding it inline.
   *
   * `replaceState`, not `pushState`: nobody asked to navigate, and a history entry for an
   * address we are correcting would make the back button undo a correction.
   */
  useEffect(() => {
    const address = addressFor(route, screen);
    if (address === null) return;

    // The route follows too when the gate sent the scorer somewhere else, so the next render
    // and the bar agree. `routeFor` rather than a hand-built route: one place decides what an
    // address means.
    const settled = routeFor(address);
    if (settled.destination !== route.destination) setRoute(settled);
    if (window.location.pathname !== address) window.history.replaceState(null, "", address);
  }, [route, screen]);

  // A signed-out player who scanned a team QR: hold the code through sign-in, then take them
  // back to it. Google sign-in returns to home and would otherwise drop the invite.
  //
  // After the effect above, deliberately: on the render where sign-in lands, that one settles
  // the route to home, and effects run in order, so this navigation has to come second to win.
  const signedIn = isSignedIn(session);
  useEffect(() => {
    const code = inviteToRemember(signedIn, destination, inviteCode);
    if (code !== null) rememberInvite(code);
  }, [destination, inviteCode, signedIn]);
  useEffect(() => {
    if (!signedIn) return;
    const code = inviteToReturnTo(destination, inviteCode, takeInvite());
    if (code !== null) go("join_team", code);
  }, [signedIn, destination, inviteCode, go]);

  useEffect(() => {
    const ask = async (): Promise<void> => {
      let answer: BootAnswer;
      let playerFromServer: PlayerId | null = null;
      try {
        const me = await api.me();
        playerFromServer = me?.playerId ?? null;
        answer = me === null ? { kind: "unauthorized" } : { kind: "user", user: me.user };
      } catch {
        // Anything other than a clean 401 — the API down, a proxy in the way, a ground with
        // no signal — means the question was never answered, and that is **not** the same as
        // "signed out". It used to be recorded as `null`, which sent a scorer mid-innings to
        // the sign-in screen he could not complete, with the rest of the match unscorable.
        answer = { kind: "unreachable" };
      }

      // The last account confirmed here, so a boot with no signal can open as them. Only the
      // server's answers move it: a network that dropped is not a reason to forget who was
      // signed in. It is a hint for the ownership check and the screens, never a credential.
      const action = hintAction(answer);
      if (action === "save" && answer.kind === "user") {
        await saveIdentityHint(answer.user).catch(() => undefined);
      } else if (action === "clear") {
        await saveIdentityHint(null).catch(() => undefined);
      }
      const hint = answer.kind === "unreachable" ? await loadIdentityHint().catch(() => null) : null;

      // A clean 401 with a guest flag on this device is not "signed out" — it is somebody
      // who said he did not want an account, and whose match is on this phone. Sending him
      // to the login gate on every reload would be the same failure #16 found, arrived at
      // from the other side. `screenFor` lets a match already on this device carry on when
      // the question was never answered.
      const resolved = bootSession(answer, hint, isGuest(store));
      provisionalRef.current = resolved.provisional;
      setPlayerId(playerFromServer);
      setSession(resolved.session);

      // A returning scorer whose cookie is still valid never touched the sign-in screen.
      // Recording it anyway is what keeps the field-test measurement honest about the
      // median path rather than only the runs that struggled (ADR-0008).
      if (answer.kind === "user") {
        friction.succeeded("returning");
        reportFriction();
      }
    };
    void ask();

    // Ask again when there is reason to think the network is back, so a session that could
    // not be confirmed at boot is upgraded to a real one rather than leaving the scorer in
    // the degraded state for the rest of the match. The same two signals the outbox uses.
    //
    // Through a ref rather than reading `session` from state: a state updater must be pure,
    // and only re-asking when the answer is still unknown avoids re-fetching `/api/me` every
    // time the scorer's phone comes back to the foreground.
    const wake = (): void => {
      // Also a provisional session — the hint stood in for an answer the server never gave,
      // and the first chance to get the real one is now.
      if (sessionRef.current === "unreachable" || provisionalRef.current) void ask();
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible") wake();
    };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  /**
   * Keep the drainer's idea of "who is signed in" in step with the session.
   *
   * A hint for the ownership check, not the gate: the session is still the cookie, still
   * asked of the server (ADR-0008). Only a confirmed answer moves it — a boot with no signal
   * (`unreachable`) leaves it alone rather than forgetting who was signed in a minute ago.
   *
   * Signing in, or an `unreachable` boot upgrading to a real session, is also the moment a
   * lapsed-session block should lift, so the queue moves without the scorer doing anything.
   * Declared before the foreground drain below so, in the commit that resolves the session,
   * the owner is set before the first drain looks at it.
   */
  useEffect(() => {
    if (session === "loading" || session === "unreachable") return;
    if (session === null || session === "guest") {
      setCurrentUserId(null);
      // Signed out, lapsed, or a guest: the next person to use this phone must not be able to
      // open the last account's matches from a saved copy.
      void readCache.purgeAll().catch(() => undefined);
      return;
    }
    setCurrentUserId(session.id);
    // A different account than the one whose copies are saved: theirs go.
    void readCache.purgeExcept(session.id).catch(() => undefined);
    void drainer.onNetworkOnline().catch(() => undefined);
  }, [session]);

  /**
   * Empty the queue for every match, whatever screen this is (see `sync/foreground.ts`).
   *
   * Started once the session question has been answered, not at mount: a drain that ran
   * before `me()` returned would send balls before it was known whose they are.
   */
  const sessionKnown = session !== "loading";
  useEffect(() => {
    if (!sessionKnown || !canPersist()) return;
    return startForegroundDrain(drainer, undefined, () => void requestBackgroundDrain());
  }, [sessionKnown]);

  /**
   * Keep the saved copies fresh while there is signal, so they are worth having when there is
   * not: on start, when the app comes to the front, and when the network returns. Starts with
   * the drain because it needs the same thing — an answer to "who is signed in".
   */
  useEffect(() => {
    if (!sessionKnown || !canPersist()) return;
    return startCacheRefresh(refreshReadCache, { window, document });
  }, [sessionKnown]);

  /**
   * Register the service worker, once, after mount.
   *
   * Does nothing in development or in a browser without support. See `sw/register.ts`.
   */
  useEffect(() => {
    whenUpdateReady((apply) => {
      // Stored as a thunk: a bare function passed to a state setter would be called as an
      // updater rather than kept as the value.
      setApplyUpdate(() => apply);
    });
    registerServiceWorker();
  }, []);

  /**
   * Resume whatever this device was scoring.
   *
   * Automatic rather than a menu the scorer has to find: a reload mid-over is an accident,
   * and the recovery has to be quicker than re-entering two squads. The ledger and the setup
   * are both on disk, so the match comes back as it was.
   *
   * Not gated on the session, deliberately. The match is on *this device*, so making its
   * recovery wait on a network round trip would leave a scorer with no signal unable to get
   * back to the innings in his hand.
   */
  useEffect(() => {
    if (!canPersist()) {
      setResuming(false);
      return;
    }
    void loadCurrentSetup().then((found) => {
      if (found !== null) {
        setSetup(found);
        // Straight back to the pad, not to the list. A reload mid-over is an accident and
        // #13's promise is that it loses nothing, which includes not losing the scorer's
        // place — landing a recovered match one tap away from the delivery being bowled is
        // still losing something.
        //
        // Unless the address bar asked for somewhere. A scorer who opened `/match/:id` or
        // `/profile` said where he wanted to be, and overriding that would make a shared link
        // un-openable for the one person most likely to be sent one: the scorer of the match.
        // The bare `/` of a fresh boot is not a request, so that one still goes to the pad.
        if (routeFor(window.location.pathname).destination === "home") {
          setRoute({ destination: "scoring", matchId: null, tournamentId: null, inviteCode: null });
          // Replace, not push. This is a redirect the scorer never asked for, and a history
          // entry for it would mean back-from-the-pad goes to the pad.
          window.history.replaceState(null, "", pathFor("scoring"));
        }
      }
      setResuming(false);
    });
  }, []);

  const startMatch = (fresh: MatchSetup): void => {
    // Written before the screen opens, for the same reason a ball is: if this device dies at
    // the first delivery, the reload has to know whose delivery it was.
    void saveSetup(fresh).then(() => {
      setSetup(fresh);
      go("scoring");
    });
  };

  /**
   * Does this phone already hold the match on screen — setup and balls — without it being the
   * one currently being scored?
   *
   * Asked of the device rather than assumed, because only the device knows. Only the signed-in
   * account's own match counts: a setup left by someone else on a shared phone is not
   * something this account may pick up and score.
   */
  const [heldHere, setHeldHere] = useState(false);
  useEffect(() => {
    setHeldHere(false);
    if (screen !== "match_detail" || matchId === null || !canPersist()) return;
    if (session === null || typeof session === "string") return;
    const accountId = session.id;
    let live = true;
    void loadSetup(matchId)
      .then((found) => {
        if (!live || found === null) return;
        setHeldHere(found.scorerId === accountId && String(found.matchId) !== String(setup?.matchId));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [screen, matchId, session, setup]);

  /**
   * Go back to scoring a match this phone already holds.
   *
   * Moves the pointer and nothing else (`makeCurrent`): the match being left keeps its setup
   * and every ball, and its unsent balls keep sending. No confirmation for that reason —
   * nothing is lost by switching, and a stray tap is undone by backing out.
   */
  const resumeHere = (id: string): void => {
    void makeCurrent(id)
      .then((found) => {
        if (found === null) return;
        setSetup(found);
        go("scoring");
      })
      .catch(() => undefined);
  };

  const newMatch = (): void => {
    // Only the pointer is cleared. The old ledger stays, unsynced balls included — deleting
    // somebody's work to tidy a menu is the failure this whole feature exists to prevent.
    // Confirmed all the same, because losing the *way back* to unsynced balls is nearly as
    // bad as losing the balls.
    if (
      setup !== null &&
      !window.confirm("Leave the match in progress and start a new one on this device?")
    ) {
      return;
    }
    void clearCurrent().then(() => {
      setSetup(null);
      go("setup");
    });
  };

  /** The server said the session is gone. Forget the hint too: it no longer describes anyone. */
  const loseSession = (): void => {
    provisionalRef.current = false;
    void saveIdentityHint(null).catch(() => undefined);
    setSession(null);
  };

  const signOut = async (): Promise<void> => {
    // Signing out never deletes a ball, but it does change who can send them: they wait for
    // this account to sign back in. Said before it happens, not discovered later.
    const waiting = await eventStore.matchesWithPending().catch(() => []);
    if (
      waiting.length > 0 &&
      !window.confirm(
        "Some balls on this phone have not reached the server yet. Signing out keeps them, but they will only send when you sign back in as this account. Sign out anyway?",
      )
    ) {
      return;
    }
    try {
      await api.signOut();
    } catch {
      // Sign-out is idempotent server-side and never 401s. If the request itself failed the
      // local session still goes: leaving a scorer looking at a signed-in screen he cannot
      // use is worse than clearing it and letting the next call re-authenticate.
    }
    // The next person on this phone must not inherit this account's offline identity.
    provisionalRef.current = false;
    await saveIdentityHint(null).catch(() => undefined);
    setSession(null);
    go("home");
    // `setup` and the ledger behind it are deliberately untouched. Signing out ends a
    // session; it does not destroy a scorer's unsent work (#14's AC-7). With #13 those balls
    // are on disk as well, so they now survive the reload that usually follows too.
  };

  // A hard stop, never a silent downgrade to memory. A scorer who is told nothing and loses
  // three hours is the exact failure this whole design exists to prevent, so a browser that
  // cannot persist is told so before a single ball is entered — and before the login gate,
  // because signing in would be wasted effort on a device that cannot score.
  if (!canPersist()) {
    return (
      <div className="setup-page">
        <div className="ds-card" role="alert" style={{ borderColor: "var(--ds-danger-500)", background: "var(--ds-danger-100)" }}>
          <strong style={{ color: "var(--ds-danger-600)" }}>This browser cannot save to the device.</strong> Scoring is disabled, because
          a match scored here could be lost. Private browsing is the usual cause — try a normal
          window.
        </div>
      </div>
    );
  }

  const guest = session === "guest";

  // `screenFor` has already decided which screens need a confirmed session. This is the same
  // conclusion in a shape the compiler can follow, since it cannot narrow through a helper.
  const user =
    session === "loading" || session === "unreachable" || session === "guest" || session === null
      ? null
      : session;

  const content = ((): React.ReactElement => {
    if (screen === "loading" || resuming) {
      return <div className="setup-page">Looking for a match on this device…</div>;
    }

    if (screen === "sign_in" || screen === "sign_up") {
      return (
        <SignIn
          // Which promise was made to this person. `/signup` says "you are new here" and
          // `/signin` says "you have been here"; opening the wrong one of the two is how a
          // returning scorer is asked for a password he never set.
          initialMode={screen === "sign_up" ? "sign_up" : "sign_in"}
          onSignedIn={(signedIn) => {
            reportFriction();
            endGuest(store);
            provisionalRef.current = false;
            void saveIdentityHint(signedIn).catch(() => undefined);
            setSession(signedIn);
            const nameIsPhone = /^\+?\d{7,15}$/.test(signedIn.displayName.replace(/\s/g, ""));
            if (nameIsPhone) setNeedsName(true);
          }}
          friction={friction}
          dark={dark}
          onToggleTheme={toggleTheme}
          onScoreAsGuest={() => {
            beginGuest(store);
            setSession("guest");
            go("home");
          }}
          // A guest who tapped through to something that needs an account has somewhere to go
          // back to; a visitor with no session does not.
          onBack={guest ? () => go("home") : null}
        />
      );
    }

    // Deliberately before the session check below: this is the one screen `screenFor` will
    // hand back without a confirmed session, because a match already on this device stays
    // scorable when the server cannot be reached. `Scoring` needs no session — the scorer id
    // is in the setup that was persisted when the match was created.
    if (screen === "scoring" && setup !== null) {
      return (
        <Scoring
          setup={setup}
          dark={dark}
          onToggleTheme={toggleTheme}
          onExit={() => go("home")}
          onMatchCompleted={() => {
            void clearCurrent().then(() => {
              setSetup(null);
            });
          }}
          // The balls are safe; this is the way to the screen that lets them move.
          onSignIn={() => {
            loseSession();
            go("sign_in");
          }}
        />
      );
    }

    // Setup is reachable by a guest, so it is decided before the account check below. What it
    // does differently is entirely inside the screen: nothing is published, and the format is
    // the engine's template rather than a row the server pinned.
    if (screen === "setup") {
      return (
        <Setup
          scorerId={user?.id ?? null}
          onStarted={(s) => { setPendingSetup(null); startMatch(s); }}
          onCancel={() => { setPendingSetup(null); go("home"); }}
          onSessionLost={loseSession}
          dark={dark}
          onToggleTheme={toggleTheme}
          initialSetup={pendingSetup}
        />
      );
    }

    if (screen === "home") {
      return (
        <Home
          userName={user?.displayName ?? null}
          onNewMatch={newMatch}
          onSessionLost={loseSession}
          resumeLabel={setup === null ? null : `${setup.teams[0].name} v ${setup.teams[1].name}`}
          onResume={() => go("scoring")}
          onOpenMatch={(id) => go("match_detail", id)}
          onOpenPublicMatch={(shortcode) => window.location.assign(publicMatchPath(shortcode))}
          onNewTournament={() => go("tournament_new")}
          onOpenTournament={(id) => go("tournament_detail", id)}
          guest={guest}
        />
      );
    }

    if (user === null) return <></>;

    if (screen === "profile") return <MyTeams />;

    if (screen === "player_stats") return <Profile user={user} playerId={playerId} />;

    if (screen === "settings") {
      return (
        <>
          <Settings
            user={user}
            dark={dark}
            onToggleTheme={toggleTheme}
            onSignOut={() => void signOut()}
            onOpenTeams={() => go("profile")}
            onOpenTournaments={() => go("tournaments")}
            onOpenStats={() => go("player_stats")}
            onEditProfile={(field) => setEditField(field)}
            onApplyUpdate={applyUpdate}
          />
          {editField !== null && (
            <EditProfileSheet
              field={editField}
              onSaved={async () => {
                setEditField(null);
                const refreshed = await api.me();
                if (refreshed) setSession(refreshed.user);
              }}
              onClose={() => setEditField(null)}
            />
          )}
        </>
      );
    }

    if (screen === "tournaments") {
      return (
        <Tournaments
          onCreateNew={() => go("tournament_new")}
          onSelect={(id) => go("tournament_detail", id)}
        />
      );
    }

    if (screen === "tournament_new") {
      return (
        <TournamentCreateWrapper
          onCreated={(id) => go("tournament_detail", id)}
          onBack={() => go("tournaments")}
        />
      );
    }

    // `idOf`, not `matchId`: #43 gave the route a `tournamentId` of its own, so one field is
    // not carrying two kinds of thing and a tournament id can never be handed to `MatchDetail`.
    if (screen === "tournament_detail" && tournamentId !== null) {
      return (
        <TournamentDetail
          tournamentId={tournamentId}
          onBack={() => go("tournaments")}
          onOpenMatch={(id) => go("match_detail", id)}
        />
      );
    }

    if (screen === "ai_assistant") {
      return <AIAssistant onBack={() => go("home")} />;
    }

    if (screen === "join_team" && inviteCode !== null) {
      return (
        <JoinTeamPage
          code={inviteCode}
          onJoined={() => go("home")}
          onBack={() => go("home")}
        />
      );
    }

    if (screen === "match_detail" && matchId !== null) {
      return (
        <MatchDetail
          matchId={matchId}
          userId={user?.id ?? null}
          onBack={() => go("home")}
          onResume={
            setup !== null && String(setup.matchId) === matchId ? () => go("scoring") : null
          }
          onResumeHere={heldHere ? () => resumeHere(matchId) : null}
          onStartScoring={user === null ? null : (id, toss) => {
            void (async () => {
              try {
                const match = await api.getMatch(id);
                const toSquad = (team: typeof match.teamA): import("./match/setup.ts").TeamSetup => ({
                  id: team.id,
                  name: team.name,
                  players: (team.squad as readonly import("@sportsos/engine").Player[]).map((p, i) => ({
                    id: p.id,
                    name: p.displayName,
                    battingOrder: i + 1,
                    inXi: true,
                    existing: true,
                    ...savedDefaults(p),
                  })),
                  keeperId: null,
                  captainId: null,
                  existing: true,
                });

                const fresh: import("./match/setup.ts").MatchSetup = {
                  matchId: match.id as any,
                  ruleSet: match.ruleSet,
                  scorerId: user.id,
                  teams: [toSquad(match.teamA), toSquad(match.teamB)],
                  tossWinnerTeamId: (toss?.winner ?? match.tossWinnerTeamId ?? match.teamA.id) as any,
                  tossDecision: toss?.decision ?? match.tossDecision ?? "bat",
                };
                setPendingSetup(fresh);
                go("setup");
              } catch {
                // Falls through — the match detail page stays
              }
            })();
          }}
        />
      );
    }

    return <></>;
  })();

  return (
    <>
      {content}
      {needsName && <NamePrompt onSaved={(name) => {
        setNeedsName(false);
        if (session !== null && session !== "loading" && session !== "unreachable" && session !== "guest") {
          void saveIdentityHint({ ...session, displayName: name }).catch(() => undefined);
          setSession({ ...session, displayName: name });
        }
      }} />}
      {/*
        Never over the pad, for the tab bar's reason: nothing lives inside the loop that is
        not the loop. Everywhere else it is persistent, because what it says — this match is
        on this phone and nowhere else — stays true until an account exists.
      */}
      <BannerStack>
      {guest && screen !== "scoring" && screen !== "loading" ? (
        <GuestBanner
          // To the profile, which for a guest *is* the gate — `screenFor` sends him there
          // because a profile is about an account. Navigating rather than clearing the guest
          // flag is what keeps the way back: a scorer who taps this mid-match and changes his
          // mind must not be stranded on a form on top of a live innings.
          onSignUp={() => go("profile")}
        />
      ) : null}
      {/* The queue, off the pad. On the pad the pill says the same and nothing crowds the loop;
          on the sign-in forms it would sit over the very button the scorer has to press. */}
      {screen !== "scoring" &&
      screen !== "loading" &&
      screen !== "sign_in" &&
      screen !== "sign_up" ? (
        <QueueBanner drainer={drainer} />
      ) : null}
      {/* Install, on the tab-bar screens only: never over the pad, never over sign-in. Last,
          so it is the top of the stack and never hides the notices above. */}
      {showsTabBar(screen) ? <InstallBanner /> : null}
      </BannerStack>
      {/*
        The bar is a function of the *screen*, not of the destination — so a scorer sent back
        to the list because his draft vanished gets the list's tab bar, and one whose session
        expired on the profile screen gets the sign-in screen with no bar over it. The rule
        that matters is the negative: never over the pad. It lives in `screen.ts`, tested.
      */}
      {showsTabBar(screen) ? (
        <TabBar active={tabFor(destination)} onNavigate={(tab) => go(DESTINATION_FOR_TAB[tab])} />
      ) : null}
    </>
  );
}

function NamePrompt({ onSaved }: { onSaved: (name: string) => void }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed.length < 2) return;
    setSaving(true);
    try {
      await api.updateProfile({ displayName: trimmed });
      onSaved(trimmed);
    } catch {
      setSaving(false);
    }
  };

  return (
    <BottomSheet title="What's your name?" onClose={() => onSaved(name.trim() || "Player")}>
      <p className="ds-caption" style={{ marginBottom: "var(--ds-space-3)" }}>
        This is the name your teammates see on the scorecard.
      </p>
      <div className="ds-field">
        <label className="ds-label" htmlFor="set-name">Your name</label>
        <input className="ds-input" id="set-name" type="text" autoComplete="name"
          placeholder="e.g. Rohit Sharma" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }} autoFocus />
      </div>
      <button className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
        style={{ marginTop: "var(--ds-space-4)" }}
        disabled={saving || name.trim().length < 2} onClick={() => void save()}>
        {saving ? "Saving..." : "Continue"}
      </button>
    </BottomSheet>
  );
}

// Before render: Chrome may offer the install prompt before anything has mounted.
captureInstallPrompt();

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

createRoot(root).render(<App />);
